import React, { useEffect, useState, useCallback, useRef, JSX } from "react";
import { fetchAndCompute, GemResult, MIN_COUNT } from "./gemLogic";
import { fetchLabGems, LabGem, GemColour } from "./labLogic";
import { fetchForbiddenJewels, ForbiddenJewel } from "./jewelsLogic";
import { fetchLevelingGems, LevelingGem } from "./levelingLogic";
import { fetchDivCards, DivCard } from "./divCardsLogic";
import { BOSSES, Boss } from "./bossingData";
import "./App.css";

type SortKey = keyof Pick<GemResult, "name" | "best" | "mid" | "worst" | "cost" | "ev" | "profit">;
type SortDir = "asc" | "desc";
type OverrideField = "best" | "mid" | "worst" | "cost";
type Tab = "double" | "lab" | "jewels" | "leveling" | "hot" | "divcards" | "bossing" | "valdo";

interface Overrides {
  [gemName: string]: Partial<Record<OverrideField, number>>;
}

const REFRESH_OPTIONS = [5, 10, 30, 60];

// ── FAVOURITES (localStorage) ────────────────────────────────────────────────
function loadFavs(key: string): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(key) ?? "[]")); } catch { return new Set(); }
}
function saveFavs(key: string, favs: Set<string>) {
  localStorage.setItem(key, JSON.stringify([...favs]));
}

// ── HELPERS ──────────────────────────────────────────────────────────────────
function fmt(val: number, decimals = 2) { return val.toFixed(decimals); }

function fmtVal(divine: number, chaos: number, overridden = false): JSX.Element {
  if (overridden || divine >= 1) return <>{fmt(divine)}<span className="currency-label">d</span></>;
  return <>{Math.round(chaos)}<span className="currency-label">c</span></>;
}

function ProfitBadge({ value }: { value: number }) {
  const color = value > 10 ? "#00e5a0" : value > 2 ? "#f0c040" : value > 0 ? "#aaa" : "#e05555";
  return <span style={{ color, fontWeight: 700 }}>{value >= 0 ? "+" : ""}{fmt(value)}d</span>;
}

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active) return <span style={{ opacity: 0.25, marginLeft: 4 }}>⇅</span>;
  return <span style={{ marginLeft: 4, color: "#c8a96e" }}>{dir === "desc" ? "↓" : "↑"}</span>;
}

function StarBtn({ active, onClick }: { active: boolean; onClick: (e: React.MouseEvent) => void }) {
  return (
    <button className={`star-btn ${active ? "star-active" : ""}`} onClick={onClick} title={active ? "Remove from favourites" : "Add to favourites"}>
      {active ? "★" : "☆"}
    </button>
  );
}

// ── EDITABLE CELL ────────────────────────────────────────────────────────────
interface EditableCellProps {
  value: number; chaos: number; overridden: boolean;
  levelStr?: string; noData?: boolean; count?: number; decimals?: number;
  onSave: (val: number) => void; onReset: () => void;
}

function EditableCell({ value, chaos, overridden, levelStr, noData, count, decimals = 2, onSave, onReset }: EditableCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  function startEdit() { setDraft(value.toFixed(decimals)); setEditing(true); }
  useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);

  function commit() {
    const parsed = parseFloat(draft);
    if (!isNaN(parsed) && parsed >= 0) onSave(parsed);
    setEditing(false);
  }
  function handleKey(e: React.KeyboardEvent) {
    if (e.key === "Enter") commit();
    if (e.key === "Escape") setEditing(false);
  }

  if (editing) {
    return (
      <td className="td-num">
        <input ref={inputRef} className="cell-input" value={draft}
          onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={handleKey}
          type="number" min="0" step="0.01" />
      </td>
    );
  }

  return (
    <td className="td-num td-editable" onClick={startEdit} title="Click to edit">
      <div className={`num-val ${overridden ? "overridden" : ""}`}>
        {fmtVal(value, chaos, overridden)}
        {noData && !overridden && <sup className="star">*</sup>}
        {overridden && (
          <button className="reset-btn" title="Reset to API value"
            onClick={(e) => { e.stopPropagation(); onReset(); }}>↺</button>
        )}
      </div>
      {levelStr && <div className="num-level">lv {levelStr}</div>}
      {count !== undefined && !overridden && <div className="num-count">~{count} listed</div>}
    </td>
  );
}

function applyOverrides(gem: GemResult, overrides: Partial<Record<OverrideField, number>>): GemResult {
  const best  = overrides.best  ?? gem.best;
  const mid   = overrides.mid   ?? gem.mid;
  const worst = overrides.worst ?? gem.worst;
  const cost  = overrides.cost  ?? gem.cost;
  const ev     = 0.25 * best + 0.5 * mid + 0.25 * worst;
  const profit = ev - cost;
  return { ...gem, best, mid, worst, cost, ev, profit };
}

// ── COLOUR STYLES ─────────────────────────────────────────────────────────────
const COLOUR_STYLE: Record<GemColour, { bg: string; text: string; label: string }> = {
  red:     { bg: "rgba(180,60,60,0.15)",   text: "#e07070", label: "Strength" },
  green:   { bg: "rgba(60,160,80,0.15)",   text: "#60c070", label: "Dexterity" },
  blue:    { bg: "rgba(60,100,200,0.15)",  text: "#7090e0", label: "Intelligence" },
  unknown: { bg: "transparent",            text: "#7a7570", label: "Unknown" },
};

function avgCost(gems: LabGem[])  { return gems.length === 0 ? 0 : gems.reduce((s, g) => s + g.cost, 0) / gems.length; }
function avgChaos(gems: LabGem[]) { return gems.length === 0 ? 0 : gems.reduce((s, g) => s + g.costChaos, 0) / gems.length; }

// ── QUANTITY CALCULATOR ───────────────────────────────────────────────────────
// Monte Carlo: E[max(X1,X2,X3)] from the gem price distribution
function expectedMax3(prices: number[], iterations = 20000): number {
  if (prices.length === 0) return 0;
  if (prices.length < 3) return Math.max(...prices);
  let total = 0;
  for (let i = 0; i < iterations; i++) {
    const a = prices[Math.floor(Math.random() * prices.length)];
    const b = prices[Math.floor(Math.random() * prices.length)];
    const c = prices[Math.floor(Math.random() * prices.length)];
    total += Math.max(a, b, c);
  }
  return total / iterations;
}

function QuantityCalc({ gems }: { gems: LabGem[] }) {
  const [runsPerHour, setRunsPerHour] = useState(6);
  const [fontsPerRun, setFontsPerRun] = useState(2);
  const [colour, setColour]           = useState<GemColour>("red");

  const pool   = gems.filter((g) => g.colour === colour);
  const prices = pool.map((g) => g.cost);
  const avgC   = avgChaos(pool);

  // E[max of 3] via Monte Carlo — memoised per colour+pool
  const eMax3 = React.useMemo(() => expectedMax3(prices), [colour, prices.length]); // eslint-disable-line

  // Cost of the input gem = cheapest gem of that colour (what you put in)
  const inputCost = prices.length > 0 ? Math.min(...prices) : 0;
  const inputCostChaos = pool.length > 0 ? Math.min(...pool.map((g) => g.costChaos)) : 0;

  // Profit per font = E[max3] - input_cost
  const profitPerFont  = eMax3 - inputCost;
  const profitPerRun   = profitPerFont * fontsPerRun;
  const profitPerHour  = profitPerRun * runsPerHour;

  const cs = COLOUR_STYLE[colour];

  function fmtD(val: number, chaosVal: number) {
    return val >= 1 ? `${val.toFixed(2)}d` : `${Math.round(chaosVal)}c`;
  }

  return (
    <div className="qty-calc">
      <div className="qty-title">⚙ Quantity Calculator</div>
      <div className="qty-body">
        <div className="qty-inputs">
          <div className="qty-field">
            <label>Runs / hour</label>
            <input type="number" min={1} max={100} value={runsPerHour}
              onChange={(e) => setRunsPerHour(Math.max(1, Number(e.target.value)))} />
          </div>
          <div className="qty-field">
            <label>Divine font / run</label>
            <input type="number" min={1} max={20} value={fontsPerRun}
              onChange={(e) => setFontsPerRun(Math.max(1, Number(e.target.value)))} />
          </div>
          <div className="qty-field">
            <label>Gem colour</label>
            <div className="qty-colour-btns">
              {(["red","green","blue"] as GemColour[]).map((c) => (
                <button key={c} className={`colour-btn colour-btn-${c} ${colour === c ? "active" : ""}`}
                  onClick={() => setColour(c)}>{COLOUR_STYLE[c].label}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="qty-results">
          <div className="qty-result-card">
            <div className="qty-result-label">E[best of 3]</div>
            <div className="qty-result-value" style={{ color: cs.text }}>
              {fmtD(eMax3, eMax3 * (avgC / (avgCost(pool) || 1)))}
            </div>
            <div className="qty-result-sub">Monte Carlo · {pool.length} gems</div>
          </div>
          <div className="qty-result-card">
            <div className="qty-result-label">Input cost / font</div>
            <div className="qty-result-value" style={{ color: "#e07070" }}>
              {fmtD(inputCost, inputCostChaos)}
            </div>
            <div className="qty-result-sub">cheapest {cs.label} gem</div>
          </div>
          <div className="qty-result-card">
            <div className="qty-result-label">Profit / font</div>
            <div className="qty-result-value" style={{ color: profitPerFont >= 0 ? "#f0c040" : "#e05555" }}>
              {profitPerFont >= 0 ? "+" : ""}{profitPerFont.toFixed(2)}d
            </div>
            <div className="qty-result-sub">E[max3] − input</div>
          </div>
          <div className="qty-result-card qty-result-highlight">
            <div className="qty-result-label">Profit / hour</div>
            <div className="qty-result-value" style={{ color: profitPerHour >= 0 ? "#00e5a0" : "#e05555" }}>
              {profitPerHour >= 0 ? "+" : ""}{profitPerHour.toFixed(2)}d
            </div>
            <div className="qty-result-sub">{runsPerHour} runs × {fontsPerRun} fonts</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── LAB RUNNER TAB ────────────────────────────────────────────────────────────

// ── REUSABLE REFRESH HOOK ────────────────────────────────────────────────────
function useAutoRefresh(fetchFn: () => Promise<void>, defaultMins = 10) {
  const [refreshMins, setRefreshMins] = useState(defaultMins);
  const [lastUpdate, setLastUpdate]   = useState<Date | null>(null);
  const [countdown, setCountdown]     = useState(0);
  const timerRef     = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  const doRefresh = useCallback(async () => {
    await fetchFn();
    setLastUpdate(new Date());
    setCountdown(refreshMins * 60);
  }, [fetchFn, refreshMins]);

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(doRefresh, refreshMins * 60 * 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [refreshMins, doRefresh]);

  useEffect(() => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, [lastUpdate]);

  const countdownStr = lastUpdate
    ? `${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, "0")}`
    : null;

  return { refreshMins, setRefreshMins, lastUpdate, countdownStr, doRefresh };
}


function LabTab({ onRefreshReady }: { onRefreshReady?: OnRefreshReady }) {
  const [gems, setGems]         = useState<LabGem[]>([]);
  const [filtered, setFiltered] = useState<LabGem[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [search, setSearch]     = useState("");
  const [sortDir, setSortDir]   = useState<SortDir>("desc");
  const [sortKey, setSortKey]   = useState<"name" | "cost">("cost");
  const [colourFilter, setColourFilter] = useState<GemColour | "all">("all");
  const [showFavsOnly, setShowFavsOnly] = useState(false);
  const [favs, setFavs]         = useState<Set<string>>(() => loadFavs("lab-favs"));
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const doFetchLab = useCallback(async () => {
    setLoading(true);
    try { const data = await fetchLabGems(); setGems(data); }
    catch { setError("Failed to fetch data."); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  const labRefresh = useAutoRefresh(doFetchLab);
  useEffect(() => {
    if (onRefreshReady) onRefreshReady({
      loading,
      lastUpdate: labRefresh.lastUpdate,
      countdownStr: labRefresh.countdownStr,
      refreshMins: labRefresh.refreshMins,
      setRefreshMins: labRefresh.setRefreshMins,
      onRefresh: labRefresh.doRefresh,
    });
  }, [labRefresh.lastUpdate, labRefresh.countdownStr, labRefresh.refreshMins, loading, onRefreshReady]); // eslint-disable-line

  useEffect(() => { doFetchLab(); }, []); // eslint-disable-line

  useEffect(() => {
    let result = [...gems];
    if (showFavsOnly) result = result.filter((g) => favs.has(g.name));
    if (colourFilter !== "all") result = result.filter((g) => g.colour === colourFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((g) => g.name.toLowerCase().includes(q));
    }
    result.sort((a, b) => {
      // favourites always first
      const aFav = favs.has(a.name) ? 0 : 1;
      const bFav = favs.has(b.name) ? 0 : 1;
      if (aFav !== bFav) return aFav - bFav;
      if (sortKey === "name") return sortDir === "asc" ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      return sortDir === "asc" ? a.cost - b.cost : b.cost - a.cost;
    });
    setFiltered(result);
    setPage(1);
  }, [gems, search, sortKey, sortDir, colourFilter, favs, showFavsOnly]);

  function toggleFav(name: string) {
    setFavs((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      saveFavs("lab-favs", next);
      return next;
    });
  }

  function handleSort(key: "name" | "cost") {
    if (key === sortKey) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  }

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const colours: GemColour[] = ["red", "green", "blue"];
  const avgs = colours.map((c) => {
    const pool = gems.filter((g) => g.colour === c);
    return { colour: c, avg: avgCost(pool), avgChaos: avgChaos(pool), count: pool.length };
  });

  return (
    <div>
      <div className="colour-summary">
        {avgs.map(({ colour, avg, avgChaos: ac, count }) => (
          <div key={colour} className="colour-summary-card" style={{ borderColor: COLOUR_STYLE[colour].text }}>
            <div className="cs-label" style={{ color: COLOUR_STYLE[colour].text }}>● {COLOUR_STYLE[colour].label}</div>
            <div className="cs-avg">{avg >= 1 ? `${avg.toFixed(2)}d` : `${Math.round(ac)}c`}</div>
            <div className="cs-count">avg cost across all {count} {COLOUR_STYLE[colour].label.toLowerCase()} gems</div>
          </div>
        ))}
      </div>

      <QuantityCalc gems={gems} />

      <div className="toolbar">
        <div className="search-wrap">
          <span className="search-icon">⌕</span>
          <input className="search" placeholder="Filter gems…" value={search} onChange={(e) => setSearch(e.target.value)} />
          {search && <button className="search-clear" onClick={() => setSearch("")}>✕</button>}
        </div>
        <div className="colour-filter">
          {(["all", "red", "green", "blue"] as const).map((c) => (
            <button key={c} className={`colour-btn colour-btn-${c} ${colourFilter === c ? "active" : ""}`}
              onClick={() => setColourFilter(c)}>
              {c === "all" ? "All" : COLOUR_STYLE[c].label}
            </button>
          ))}
        </div>
        <button className={`fav-filter-btn ${showFavsOnly ? "active" : ""}`} onClick={() => setShowFavsOnly((v) => !v)}>
          ★ Favourites {favs.size > 0 && `(${favs.size})`}
        </button>
        <div className="toolbar-right">
          <div className="result-count">{filtered.length} / {gems.length} gems</div>
        </div>
      </div>

      {error && <div className="error-banner">⚠ {error}</div>}

      <div className="table-wrap">
        <table className="gem-table lab-table">
          <thead>
            <tr>
              <th className="th-rank">#</th>
              <th style={{ width: 32 }}></th>
              <th className={`th-sortable ${sortKey === "name" ? "th-active" : ""}`} onClick={() => handleSort("name")} style={{ textAlign: "left" }}>
                Gem <SortIcon active={sortKey === "name"} dir={sortDir} />
              </th>
              <th className={`th-sortable ${sortKey === "cost" ? "th-active" : ""}`} onClick={() => handleSort("cost")}>
                Cost <SortIcon active={sortKey === "cost"} dir={sortDir} />
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="loading-cell"><div className="spinner" /><div>Fetching data…</div></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={4} className="empty-cell">No gems found.</td></tr>
            ) : (
              filtered.slice((page - 1) * pageSize, page * pageSize).map((g, i) => {
                const idx = (page - 1) * pageSize + i;
                const cs  = COLOUR_STYLE[g.colour];
                const isFav = favs.has(g.name);
                return (
                  <tr key={g.name} className={`gem-row ${idx % 2 === 0 ? "row-even" : "row-odd"} ${isFav ? "row-fav" : ""}`}
                    style={{ borderLeft: `3px solid ${cs.text}` }}>
                    <td className="td-rank">{idx + 1}</td>
                    <td className="td-fav">
                      <StarBtn active={isFav} onClick={(e) => { e.stopPropagation(); toggleFav(g.name); }} />
                    </td>
                    <td className="td-name">
                      {g.icon && <img src={g.icon} alt="" className="gem-icon" />}
                      <span>{g.name}</span>
                    </td>
                    <td className="td-num">
                      <div className="num-val">{fmtVal(g.cost, g.costChaos)}</div>
                      <div className="num-count">~{g.count} listed</div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="page-size-control">
          <span className="page-label">Rows:</span>
          {[10, 25, 50, 100].map((s) => (
            <button key={s} className={`page-size-btn ${pageSize === s ? "active" : ""}`}
              onClick={() => { setPageSize(s); setPage(1); }}>{s}</button>
          ))}
        </div>
        <div className="page-nav">
          <button className="page-btn" onClick={() => setPage(1)} disabled={page === 1}>«</button>
          <button className="page-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
            .reduce<(number | "…")[]>((acc, p, idx, arr) => {
              if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("…");
              acc.push(p); return acc;
            }, [])
            .map((p, idx) => p === "…"
              ? <span key={`d${idx}`} className="page-dots">…</span>
              : <button key={p} className={`page-btn ${page === p ? "active" : ""}`} onClick={() => setPage(p as number)}>{p}</button>
            )}
          <button className="page-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
          <button className="page-btn" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
        </div>
        <div className="page-info">{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)} of {filtered.length}</div>
      </div>
    </div>
  );
}


// ── CHAOS↔DIVINE CONVERTER (used in header) ──────────────────────────────────
function useChaosPerDiv(league = "Allflame"): number {
  const [rate, setRate] = useState(0);
  useEffect(() => {
    fetch(`/api/currency?league=${encodeURIComponent(league)}`)
      .then((r) => r.json())
      .then((data) => {
        const lines: any[] = data.lines ?? [];
        const div = lines.find((l) => l.currencyTypeName === "Divine Orb");
        if (div) setRate(div.chaosEquivalent ?? 0);
      })
      .catch(() => {});
  }, []); // eslint-disable-line
  return rate;
}

function ChaosConverter({ chaosPerDiv }: { chaosPerDiv: number }) {
  const [input, setInput] = useState("");
  const [mode, setMode] = useState<"c2d" | "d2c">("c2d");

  const numVal = parseFloat(input) || 0;
  const result = mode === "c2d"
    ? (chaosPerDiv > 0 ? numVal / chaosPerDiv : 0)
    : numVal * chaosPerDiv;
  const resultLabel = mode === "c2d" ? "d" : "c";
  const inputLabel  = mode === "c2d" ? "chaos" : "divine";

  if (chaosPerDiv === 0) return null;

  return (
    <div className="converter">
      <span className="converter-rate">1d = {Math.round(chaosPerDiv)}c</span>
      <button className="converter-toggle" onClick={() => setMode((m) => m === "c2d" ? "d2c" : "c2d")}
        title="Swap direction">⇄</button>
      <input className="converter-input" type="number" min="0" step="1"
        placeholder={inputLabel} value={input}
        onChange={(e) => setInput(e.target.value)} />
      <span className="converter-result">
        = {result > 0 ? (resultLabel === "d" ? result.toFixed(2) : Math.round(result)) : "—"}{resultLabel}
      </span>
    </div>
  );
}


// ── PAIR CALCULATOR ───────────────────────────────────────────────────────────
function PairCalculator({ jewels }: { jewels: ForbiddenJewel[] }) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Get unique node names
  const nodes = Array.from(new Set(jewels.map((j) => j.name))).sort();
  const filtered = search.trim()
    ? nodes.filter((n) => n.toLowerCase().includes(search.toLowerCase()))
    : nodes;

  // Close dropdown on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setShowDropdown(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const flame = jewels.find((j) => j.name === selected && j.type === "flame");
  const flesh  = jewels.find((j) => j.name === selected && j.type === "flesh");
  const total  = (flame?.cost ?? 0) + (flesh?.cost ?? 0);
  const totalC = (flame?.costChaos ?? 0) + (flesh?.costChaos ?? 0);

  return (
    <div className="pair-calc">
      <div className="qty-title">💎 Pair Calculator</div>
      <div className="pair-body">
        <div className="pair-left">
          <div className="pair-search-wrap" ref={ref}>
            <input
              className="pair-search"
              placeholder="Search node name…"
              value={selected ?? search}
              onChange={(e) => { setSearch(e.target.value); setSelected(null); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
            />
            {showDropdown && filtered.length > 0 && (
              <div className="pair-dropdown">
                {filtered.slice(0, 10).map((n) => (
                  <div key={n} className="pair-option" onClick={() => { setSelected(n); setSearch(""); setShowDropdown(false); }}>
                    {n}
                  </div>
                ))}
              </div>
            )}
          </div>
          {selected && <button className="pair-clear" onClick={() => { setSelected(null); setSearch(""); }}>✕ Clear</button>}
        </div>

        <div className="pair-results">
          {[{ label: "🔥 Forbidden Flame", entry: flame, col: "#e07070" },
            { label: "💧 Forbidden Flesh", entry: flesh,  col: "#7090e0" }].map(({ label, entry, col }) => (
            <div key={label} className="pair-card" style={{ borderColor: col }}>
              <div className="pair-card-label" style={{ color: col }}>{label}</div>
              {entry ? (
                <>
                  <div className="pair-card-price">{fmtVal(entry.cost, entry.costChaos)}</div>
                  <div className="pair-card-count">~{entry.count} listed</div>
                </>
              ) : (
                <div className="pair-card-price" style={{ color: "#666" }}>
                  {selected ? "No data" : "—"}
                </div>
              )}
            </div>
          ))}
          <div className="pair-card pair-card-total">
            <div className="pair-card-label" style={{ color: "#00e5a0" }}>Total pair cost</div>
            <div className="pair-card-price" style={{ color: "#00e5a0" }}>
              {selected && (flame || flesh) ? fmtVal(total, totalC) : "—"}
            </div>
            <div className="pair-card-count">{selected ?? "Select a node"}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── FORBIDDEN JEWELS TAB ──────────────────────────────────────────────────────
function JewelsTab({ onRefreshReady }: { onRefreshReady?: OnRefreshReady }) {
  const [jewels, setJewels]       = useState<ForbiddenJewel[]>([]);
  const [filtered, setFiltered]   = useState<ForbiddenJewel[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [search, setSearch]       = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | "flame" | "flesh">("all");
  const [sortKey, setSortKey]     = useState<"variant" | "cost">("cost");
  const [sortDir, setSortDir]     = useState<"asc" | "desc">("desc");
  const [favs, setFavs]           = useState<Set<string>>(() => loadFavs("jewel-favs"));
  const [showFavsOnly, setShowFavsOnly] = useState(false);
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState(25);

  const doFetchJewels = useCallback(async () => {
    setLoading(true);
    try { const d = await fetchForbiddenJewels(); setJewels(d); }
    catch { setError("Failed to fetch jewels."); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line
  const jewelsRefresh = useAutoRefresh(doFetchJewels);
  useEffect(() => {
    if (onRefreshReady) onRefreshReady({
      loading,
      lastUpdate: jewelsRefresh.lastUpdate,
      countdownStr: jewelsRefresh.countdownStr,
      refreshMins: jewelsRefresh.refreshMins,
      setRefreshMins: jewelsRefresh.setRefreshMins,
      onRefresh: jewelsRefresh.doRefresh,
    });
  }, [jewelsRefresh.lastUpdate, jewelsRefresh.countdownStr, jewelsRefresh.refreshMins, loading, onRefreshReady]); // eslint-disable-line
  useEffect(() => { doFetchJewels(); }, []); // eslint-disable-line

  useEffect(() => {
    let result = [...jewels];
    if (typeFilter !== "all") result = result.filter((j) => j.type === typeFilter);
    if (showFavsOnly) result = result.filter((j) => favs.has(j.name + j.type));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((j) => j.name.toLowerCase().includes(q) || j.ascendancy.toLowerCase().includes(q));
    }
    result.sort((a, b) => {
      const av = sortKey === "variant" ? a.name : a.cost;
      const bv = sortKey === "variant" ? b.name : b.cost;
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv as string) : (bv as string).localeCompare(av);
      return sortDir === "asc" ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
    setFiltered(result);
    setPage(1);
  }, [jewels, typeFilter, search, sortKey, sortDir, favs, showFavsOnly]);

  function toggleFav(key: string) {
    setFavs((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      saveFavs("jewel-favs", next);
      return next;
    });
  }

  function handleSort(key: "variant" | "cost") {
    if (key === sortKey) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir(key === "cost" ? "desc" : "asc"); }
  }

  const flames = jewels.filter((j) => j.type === "flame");
  const flesh  = jewels.filter((j) => j.type === "flesh");
  const avgDiv = (arr: ForbiddenJewel[]) => arr.length === 0 ? 0 : arr.reduce((s, j) => s + j.cost, 0) / arr.length;
  const avgC   = (arr: ForbiddenJewel[]) => arr.length === 0 ? 0 : arr.reduce((s, j) => s + j.costChaos, 0) / arr.length;

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  return (
    <div>
      {/* Averages */}
      <div className="colour-summary">
        {[{ label: "🔥 Forbidden Flame", arr: flames, col: "#e07070" },
          { label: "💧 Forbidden Flesh", arr: flesh, col: "#7090e0" }].map(({ label, arr, col }) => (
          <div key={label} className="colour-summary-card" style={{ borderColor: col }}>
            <div className="cs-label" style={{ color: col }}>{label}</div>
            <div className="cs-avg">
              {avgDiv(arr) >= 1 ? `${avgDiv(arr).toFixed(2)}d` : `${Math.round(avgC(arr))}c`}
            </div>
            <div className="cs-count">avg · {arr.length} nodes</div>
          </div>
        ))}
      </div>

      {/* Pair Calculator */}
      <PairCalculator jewels={jewels} />

      <div className="toolbar">
        <div className="search-wrap">
          <span className="search-icon">⌕</span>
          <input className="search" placeholder="Search node or ascendancy…" value={search}
            onChange={(e) => setSearch(e.target.value)} />
          {search && <button className="search-clear" onClick={() => setSearch("")}>✕</button>}
        </div>
        <div className="colour-filter">
          {(["all","flame","flesh"] as const).map((t) => (
            <button key={t} className={`colour-btn colour-btn-${t === "flame" ? "red" : t === "flesh" ? "blue" : "all"} ${typeFilter === t ? "active" : ""}`}
              onClick={() => setTypeFilter(t)}>
              {t === "all" ? "All" : t === "flame" ? "🔥 Flame" : "💧 Flesh"}
            </button>
          ))}
        </div>
        <button className={`fav-filter-btn ${showFavsOnly ? "active" : ""}`} onClick={() => setShowFavsOnly((v) => !v)}>
          ★ Favourites {favs.size > 0 && `(${favs.size})`}
        </button>
        <div className="toolbar-right">
          <div className="result-count">{filtered.length} / {jewels.length} jewels</div>
        </div>
      </div>

      {error && <div className="error-banner">⚠ {error}</div>}

      <div className="table-wrap">
        <table className="gem-table">
          <thead>
            <tr>
              <th className="th-rank">#</th>
              <th style={{ width: 32 }}></th>
              <th className={`th-sortable ${sortKey === "variant" ? "th-active" : ""}`}
                onClick={() => handleSort("variant")} style={{ textAlign: "left" }}>
                Node / Ascendancy <SortIcon active={sortKey === "variant"} dir={sortDir} />
              </th>
              <th className="th-sortable">Type</th>
              <th className={`th-sortable ${sortKey === "cost" ? "th-active" : ""}`}
                onClick={() => handleSort("cost")}>
                Price <SortIcon active={sortKey === "cost"} dir={sortDir} />
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="loading-cell"><div className="spinner" /><div>Fetching jewels…</div></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="empty-cell">No jewels found.</td></tr>
            ) : (
              filtered.slice((page-1)*pageSize, page*pageSize).map((j, i) => {
                const key = j.name + j.type;
                const isFav = favs.has(key);
                const col = j.type === "flame" ? "#e07070" : "#7090e0";
                return (
                  <tr key={key} className={`gem-row ${i%2===0?"row-even":"row-odd"} ${isFav?"row-fav":""}`}
                    style={{ borderLeft: `3px solid ${col}` }}>
                    <td className="td-rank">{(page-1)*pageSize+i+1}</td>
                    <td className="td-fav">
                      <StarBtn active={isFav} onClick={(e) => { e.stopPropagation(); toggleFav(key); }} />
                    </td>
                    <td className="td-name">
                      {j.icon && <img src={j.icon} alt="" className="gem-icon" />}
                      <div>
                        <div>{j.name}</div>
                        <div className="num-level">{j.ascendancy}</div>
                      </div>
                    </td>
                    <td className="td-num" style={{ color: col, fontSize: 12 }}>
                      {j.type === "flame" ? "🔥 Flame" : "💧 Flesh"}
                    </td>
                    <td className="td-num">
                      <div className="num-val">{fmtVal(j.cost, j.costChaos)}</div>
                      <div className="num-count">~{j.count} listed</div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} pageSize={pageSize}
        total={filtered.length} onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />
    </div>
  );
}

// ── GEM LEVELING TAB ──────────────────────────────────────────────────────────
function LevelingTab({ onRefreshReady }: { onRefreshReady?: OnRefreshReady }) {
  const [gems, setGems]           = useState<LevelingGem[]>([]);
  const [filtered, setFiltered]   = useState<LevelingGem[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [search, setSearch]       = useState("");
  const [sortKey, setSortKey]     = useState<"name"|"buyPrice"|"sellPrice"|"profit"|"profitPct">("profit");
  const [sortDir, setSortDir]     = useState<"asc"|"desc">("desc");
  const [favs, setFavs]           = useState<Set<string>>(() => loadFavs("leveling-favs"));
  const [showFavsOnly, setShowFavsOnly] = useState(false);
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState(25);

  const doFetchLeveling = useCallback(async () => {
    setLoading(true);
    try { const d = await fetchLevelingGems(); setGems(d); }
    catch { setError("Failed to fetch gems."); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line
  const levelingRefresh = useAutoRefresh(doFetchLeveling);
  useEffect(() => {
    if (onRefreshReady) onRefreshReady({
      loading,
      lastUpdate: levelingRefresh.lastUpdate,
      countdownStr: levelingRefresh.countdownStr,
      refreshMins: levelingRefresh.refreshMins,
      setRefreshMins: levelingRefresh.setRefreshMins,
      onRefresh: levelingRefresh.doRefresh,
    });
  }, [levelingRefresh.lastUpdate, levelingRefresh.countdownStr, levelingRefresh.refreshMins, loading, onRefreshReady]); // eslint-disable-line
  useEffect(() => { doFetchLeveling(); }, []); // eslint-disable-line

  useEffect(() => {
    let result = [...gems];
    if (showFavsOnly) result = result.filter((g) => favs.has(g.name));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((g) => g.name.toLowerCase().includes(q));
    }
    result.sort((a, b) => {
      const aFav = favs.has(a.name) ? 0 : 1;
      const bFav = favs.has(b.name) ? 0 : 1;
      if (aFav !== bFav) return aFav - bFav;
      const av = a[sortKey] as any, bv = b[sortKey] as any;
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? av - bv : bv - av;
    });
    setFiltered(result);
    setPage(1);
  }, [gems, search, sortKey, sortDir, favs, showFavsOnly]);

  function toggleFav(name: string) {
    setFavs((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      saveFavs("leveling-favs", next);
      return next;
    });
  }

  function handleSort(key: typeof sortKey) {
    if (key === sortKey) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  }

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  const cols: { key: typeof sortKey; label: string }[] = [
    { key: "name", label: "Gem" },
    { key: "buyPrice", label: "Buy (lv1 q20)" },
    { key: "sellPrice", label: "Sell (max lv q20)" },
    { key: "profit", label: "Profit" },
    { key: "profitPct", label: "ROI %" },
  ];

  return (
    <div>
      <div className="toolbar">
        <div className="search-wrap">
          <span className="search-icon">⌕</span>
          <input className="search" placeholder="Filter gems…" value={search}
            onChange={(e) => setSearch(e.target.value)} />
          {search && <button className="search-clear" onClick={() => setSearch("")}>✕</button>}
        </div>
        <button className={`fav-filter-btn ${showFavsOnly ? "active" : ""}`} onClick={() => setShowFavsOnly((v) => !v)}>
          ★ Favourites {favs.size > 0 && `(${favs.size})`}
        </button>
        <div className="toolbar-right">
          <div className="result-count">{filtered.length} / {gems.length} gems</div>
          <div className="legend" style={{ color: "#7a7570", fontSize: 11 }}>
            lv1 q20 → lv20 q20 · non-corrupted · excludes exceptional supports
          </div>
        </div>
      </div>


      {error && <div className="error-banner">⚠ {error}</div>}

      <div className="table-wrap">
        <table className="gem-table">
          <thead>
            <tr>
              <th className="th-rank">#</th>
              <th style={{ width: 32 }}></th>
              {cols.map((c) => (
                <th key={c.key} className={`th-sortable ${sortKey === c.key ? "th-active" : ""}`}
                  onClick={() => handleSort(c.key)}
                  style={c.key === "name" ? { textAlign: "left" } : {}}>
                  {c.label} <SortIcon active={sortKey === c.key} dir={sortDir} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="loading-cell"><div className="spinner" /><div>Fetching data…</div></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="empty-cell">No gems found.</td></tr>
            ) : (
              filtered.slice((page-1)*pageSize, page*pageSize).map((g, i) => {
                const idx = (page-1)*pageSize+i;
                const isFav = favs.has(g.name);
                const profitColor = g.profit > 5 ? "#00e5a0" : g.profit > 1 ? "#f0c040" : g.profit > 0 ? "#aaa" : "#e05555";
                return (
                  <tr key={g.name} className={`gem-row ${idx%2===0?"row-even":"row-odd"} ${isFav?"row-fav":""}`}>
                    <td className="td-rank">{idx+1}</td>
                    <td className="td-fav">
                      <StarBtn active={isFav} onClick={(e) => { e.stopPropagation(); toggleFav(g.name); }} />
                    </td>
                    <td className="td-name">
                      {g.icon && <img src={g.icon} alt="" className="gem-icon" />}
                      <span>{g.name}</span>
                    </td>
                    <td className="td-num">
                      <div className="num-val">{fmtVal(g.buyPrice, g.buyChaos)}</div>
                      <div className="num-count">~{g.buyCount} listed</div>
                    </td>
                    <td className="td-num">
                      <div className="num-val">{fmtVal(g.sellPrice, g.sellChaos)}</div>
                      <div className="num-count">lv{g.maxLevel}/20 · ~{g.sellCount} listed</div>
                    </td>
                    <td className="td-num">
                      <div className="num-val" style={{ color: profitColor, fontWeight: 700 }}>
                        {g.profit >= 0 ? "+" : ""}{fmtVal(g.profit, g.profit >= 0 ? 0 : 0)}
                      </div>
                    </td>
                    <td className="td-num">
                      <div className="num-val" style={{ color: profitColor }}>
                        {g.profitPct >= 0 ? "+" : ""}{g.profitPct.toFixed(0)}%
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} pageSize={pageSize}
        total={filtered.length} onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />
    </div>
  );
}

// ── SHARED PAGINATION ─────────────────────────────────────────────────────────
function PaginationBar({ page, totalPages, pageSize, total, onPage, onPageSize }:
  { page: number; totalPages: number; pageSize: number; total: number;
    onPage: (p: number) => void; onPageSize: (s: number) => void }) {
  return (
    <div className="pagination">
      <div className="page-size-control">
        <span className="page-label">Rows:</span>
        {[10, 25, 50, 100].map((s) => (
          <button key={s} className={`page-size-btn ${pageSize === s ? "active" : ""}`}
            onClick={() => onPageSize(s)}>{s}</button>
        ))}
      </div>
      <div className="page-nav">
        <button className="page-btn" onClick={() => onPage(1)} disabled={page === 1}>«</button>
        <button className="page-btn" onClick={() => onPage(Math.max(1, page-1))} disabled={page === 1}>‹</button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
          .reduce<(number | "…")[]>((acc, p, idx, arr) => {
            if (idx > 0 && p - (arr[idx-1] as number) > 1) acc.push("…");
            acc.push(p); return acc;
          }, [])
          .map((p, idx) => p === "…"
            ? <span key={`d${idx}`} className="page-dots">…</span>
            : <button key={p} className={`page-btn ${page === p ? "active" : ""}`} onClick={() => onPage(p as number)}>{p}</button>
          )}
        <button className="page-btn" onClick={() => onPage(Math.min(totalPages, page+1))} disabled={page === totalPages}>›</button>
        <button className="page-btn" onClick={() => onPage(totalPages)} disabled={page === totalPages}>»</button>
      </div>
      <div className="page-info">{(page-1)*pageSize+1}–{Math.min(page*pageSize, total)} of {total}</div>
    </div>
  );
}



// ── HOT ITEMS TAB ─────────────────────────────────────────────────────────────
interface HotItem {
  name: string;
  icon?: string;
  cost: number;
  costChaos: number;
  change7d: number;
  category: string;
  count: number;
}

async function fetchHotItems(league = "Allflame"): Promise<HotItem[]> {
  const [gemsRes, jewelsRes, uniquesRes] = await Promise.all([
    fetch(`/api/gems?league=${encodeURIComponent(league)}`).then((r) => r.json()),
    fetch(`/api/jewels?league=${encodeURIComponent(league)}`).then((r) => r.json()),
    fetch(`/api/uniques?league=${encodeURIComponent(league)}`).then((r) => r.json()),
  ]);

  const results: HotItem[] = [];

  function getChange(raw: any): number {
    // Prefer lowConfidenceSparkLine — it updates more frequently (near real-time)
    const lc = raw.lowConfidenceSparkLine?.data ?? [];
    const recent = lc.filter((v: any) => v !== null);
    if (recent.length >= 2) {
      const first = recent[0], last = recent[recent.length - 1];
      if (first !== 0 && first != null) return ((last - first) / Math.abs(first)) * 100;
    }
    return raw.lowConfidenceSparkLine?.totalChange ?? raw.sparkLine?.totalChange ?? 0;
  }

  // Gems
  for (const raw of gemsRes.lines ?? []) {
    const change = getChange(raw);
    if (Math.abs(change) < 20) continue;
    results.push({
      name: `${raw.name} (${raw.variant ?? `lv${raw.gemLevel}`})`,
      icon: raw.icon,
      cost: raw.divineValue ?? 0,
      costChaos: raw.chaosValue ?? 0,
      change7d: change,
      category: "Skill Gem",
      count: raw.count ?? 0,
    });
  }

  // Jewels
  for (const raw of jewelsRes.lines ?? []) {
    const change = getChange(raw);
    if (Math.abs(change) < 20) continue;
    const meta = raw.metadata ?? {};
    results.push({
      name: `${meta.passiveName ?? raw.name}${raw.variant ? ` (${raw.variant})` : ""}`,
      icon: raw.icon,
      cost: raw.divineValue ?? 0,
      costChaos: raw.chaosValue ?? 0,
      change7d: change,
      category: "Jewel",
      count: raw.count ?? 0,
    });
  }

  // Uniques
  for (const raw of uniquesRes.lines ?? []) {
    const change = getChange(raw);
    if (Math.abs(change) < 20) continue;
    results.push({
      name: raw.name + (raw.links ? ` (${raw.links}L)` : raw.variant ? ` (${raw.variant})` : ""),
      icon: raw.icon,
      cost: raw.divineValue ?? 0,
      costChaos: raw.chaosValue ?? 0,
      change7d: change,
      category: raw.itemType ?? "Unique",
      count: raw.count ?? 0,
    });
  }

  results.sort((a, b) => Math.abs(b.change7d) - Math.abs(a.change7d));
  return results;
}

function HotTab({ onRefreshReady }: { onRefreshReady?: OnRefreshReady }) {
  const [items, setItems]         = useState<HotItem[]>([]);
  const [filtered, setFiltered]   = useState<HotItem[]>([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [direction, setDirection] = useState<"all" | "up" | "down">("all");
  const [cats, setCats]           = useState<Set<string>>(new Set()); // empty = all
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState(25);

  const doFetchHot = useCallback(async () => {
    setLoading(true);
    try { const d = await fetchHotItems(); setItems(d); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line
  const hotRefresh = useAutoRefresh(doFetchHot);
  useEffect(() => {
    if (onRefreshReady) onRefreshReady({
      loading,
      lastUpdate: hotRefresh.lastUpdate,
      countdownStr: hotRefresh.countdownStr,
      refreshMins: hotRefresh.refreshMins,
      setRefreshMins: hotRefresh.setRefreshMins,
      onRefresh: hotRefresh.doRefresh,
    });
  }, [hotRefresh.lastUpdate, hotRefresh.countdownStr, hotRefresh.refreshMins, loading, onRefreshReady]); // eslint-disable-line
  useEffect(() => { doFetchHot(); }, []); // eslint-disable-line

  // Get actual categories present in data
  const availableCats = Array.from(new Set(items.map((i) => i.category))).sort();

  function toggleCat(cat: string) {
    setCats((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
    setPage(1);
  }

  useEffect(() => {
    let result = [...items];
    if (direction === "up")   result = result.filter((i) => i.change7d > 0);
    if (direction === "down") result = result.filter((i) => i.change7d < 0);
    if (cats.size > 0) result = result.filter((i) => cats.has(i.category));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((i) => i.name.toLowerCase().includes(q));
    }
    setFiltered(result);
    setPage(1);
  }, [items, search, direction, cats]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  return (
    <div>
      <div className="toolbar" style={{ flexWrap: "wrap", gap: 8 }}>
        <div className="search-wrap">
          <span className="search-icon">⌕</span>
          <input className="search" placeholder="Search items…" value={search}
            onChange={(e) => setSearch(e.target.value)} />
          {search && <button className="search-clear" onClick={() => setSearch("")}>✕</button>}
        </div>

        {/* Direction — mutually exclusive */}
        <div className="colour-filter">
          <span className="refresh-label">Direction:</span>
          {(["all","up","down"] as const).map((m) => (
            <button key={m}
              className={`colour-btn colour-btn-${m === "up" ? "green" : m === "down" ? "red" : "all"} ${direction === m ? "active" : ""}`}
              onClick={() => setDirection(m)}>
              {m === "all" ? "All" : m === "up" ? "📈 Rising" : "📉 Falling"}
            </button>
          ))}
        </div>

        <div className="toolbar-right">
          <div className="result-count">{filtered.length} / {items.length} items</div>
        </div>
      </div>

      {/* Category filters — multi-select */}
      <div className="hot-cats">
        <span className="refresh-label" style={{ flexShrink: 0 }}>Category:</span>
        <button className={`colour-btn colour-btn-all ${cats.size === 0 ? "active" : ""}`}
          onClick={() => setCats(new Set())}>All</button>
        {availableCats.map((c) => (
          <button key={c}
            className={`colour-btn ${cats.has(c) ? "cat-btn-active" : ""}`}
            onClick={() => toggleCat(c)}>
            {c}
          </button>
        ))}
      </div>

      <div className="table-wrap">
        <table className="gem-table">
          <thead>
            <tr>
              <th className="th-rank">#</th>
              <th className="th-sortable" style={{ textAlign: "left" }}>Item</th>
              <th className="th-sortable">Category</th>
              <th className="th-sortable">Price</th>
              <th className="th-sortable th-active">Recent Change</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="loading-cell"><div className="spinner" /><div>Fetching data…</div></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="empty-cell">No items found.</td></tr>
            ) : (
              filtered.slice((page-1)*pageSize, page*pageSize).map((item, i) => {
                const idx = (page-1)*pageSize+i;
                const up = item.change7d > 0;
                const changeColor = up ? "#00e5a0" : "#e05555";
                return (
                  <tr key={item.name + idx} className={`gem-row ${idx%2===0?"row-even":"row-odd"}`}>
                    <td className="td-rank">{idx+1}</td>
                    <td className="td-name">
                      {item.icon && <img src={item.icon} alt="" className="gem-icon" />}
                      <div>
                        <div>{item.name}</div>
                        <div className="num-count">~{item.count} listed</div>
                      </div>
                    </td>
                    <td className="td-num" style={{ fontSize: 11, color: "#7a7570" }}>{item.category}</td>
                    <td className="td-num">
                      <div className="num-val">{fmtVal(item.cost, item.costChaos)}</div>
                    </td>
                    <td className="td-num">
                      <div className="num-val" style={{ color: changeColor, fontWeight: 700, fontSize: 16 }}>
                        {up ? "▲" : "▼"} {Math.abs(item.change7d).toFixed(1)}%
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} pageSize={pageSize}
        total={filtered.length} onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />
    </div>
  );
}


// ── DIVINATION CARD FLIPPER TAB ───────────────────────────────────────────────
function DivCardsTab({ onRefreshReady }: { onRefreshReady?: OnRefreshReady }) {
  const [cards, setCards]         = useState<DivCard[]>([]);
  const [filtered, setFiltered]   = useState<DivCard[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [search, setSearch]       = useState("");
  const [typeFilter, setTypeFilter] = useState<"all"|"currency"|"gem"|"unique">("all");
  const [showAll, setShowAll]     = useState(false);
  const [sortKey, setSortKey]     = useState<"profit"|"profitPct"|"totalCost"|"rewardValue"|"name">("profit");
  const [sortDir, setSortDir]     = useState<"asc"|"desc">("desc");
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState(25);

  const doFetchDivCards = useCallback(async () => {
    setLoading(true);
    try { const d = await fetchDivCards(); setCards(d); }
    catch { setError("Failed to fetch data."); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line
  const divCardsRefresh = useAutoRefresh(doFetchDivCards);
  useEffect(() => {
    if (onRefreshReady) onRefreshReady({
      loading,
      lastUpdate: divCardsRefresh.lastUpdate,
      countdownStr: divCardsRefresh.countdownStr,
      refreshMins: divCardsRefresh.refreshMins,
      setRefreshMins: divCardsRefresh.setRefreshMins,
      onRefresh: divCardsRefresh.doRefresh,
    });
  }, [divCardsRefresh.lastUpdate, divCardsRefresh.countdownStr, divCardsRefresh.refreshMins, loading, onRefreshReady]); // eslint-disable-line
  useEffect(() => { doFetchDivCards(); }, []); // eslint-disable-line

  useEffect(() => {
    let result = [...cards];
    if (!showAll) result = result.filter((c) => c.profit > 0);
    if (typeFilter !== "all") result = result.filter((c) => c.rewardType === typeFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((c) => c.name.toLowerCase().includes(q) || c.rewardText.toLowerCase().includes(q));
    }
    result.sort((a, b) => {
      const av = a[sortKey] as any, bv = b[sortKey] as any;
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? av - bv : bv - av;
    });
    setFiltered(result);
    setPage(1);
  }, [cards, search, typeFilter, showAll, sortKey, sortDir]);

  function handleSort(key: typeof sortKey) {
    if (key === sortKey) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  }

  const typeColors: Record<string, string> = { currency: "#f0c040", gem: "#4a90d9", unique: "#c8a96e", unknown: "#666" };
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  const cols: { key: typeof sortKey; label: string }[] = [
    { key: "name", label: "Card" },
    { key: "totalCost", label: "Cost (full set)" },
    { key: "rewardValue", label: "Reward value" },
    { key: "profit", label: "Profit" },
    { key: "profitPct", label: "ROI %" },
  ];

  return (
    <div>
      <div className="toolbar">
        <div className="search-wrap">
          <span className="search-icon">⌕</span>
          <input className="search" placeholder="Search card or reward…" value={search}
            onChange={(e) => setSearch(e.target.value)} />
          {search && <button className="search-clear" onClick={() => setSearch("")}>✕</button>}
        </div>
        <div className="colour-filter">
          {(["all","currency","gem","unique"] as const).map((t) => (
            <button key={t}
              className={`colour-btn ${typeFilter === t ? "cat-btn-active" : ""}`}
              style={ typeFilter === t && t !== "all" ? { borderColor: typeColors[t], color: typeColors[t] } : {}}
              onClick={() => setTypeFilter(t)}>
              {t === "all" ? "All" : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
        <button className={`fav-filter-btn ${showAll ? "active" : ""}`} onClick={() => setShowAll((v) => !v)}>
          {showAll ? "Profitable only" : "Show all"}
        </button>
        <div className="toolbar-right">
          <div className="result-count">{filtered.length} cards</div>
          <div className="legend" style={{ fontSize: 11, color: "#7a7570" }}>
            Profit = reward value − cost of full set
          </div>
        </div>
      </div>

      {error && <div className="error-banner">⚠ {error}</div>}

      <div className="table-wrap">
        <table className="gem-table">
          <thead>
            <tr>
              <th className="th-rank">#</th>
              {cols.map((c) => (
                <th key={c.key} className={`th-sortable ${sortKey === c.key ? "th-active" : ""}`}
                  onClick={() => handleSort(c.key)}
                  style={c.key === "name" ? { textAlign: "left" } : {}}>
                  {c.label} <SortIcon active={sortKey === c.key} dir={sortDir} />
                </th>
              ))}
              <th className="th-sortable">Reward</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="loading-cell"><div className="spinner" /><div>Fetching cards…</div></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="empty-cell">No cards found.</td></tr>
            ) : (
              filtered.slice((page-1)*pageSize, page*pageSize).map((c, i) => {
                const idx = (page-1)*pageSize+i;
                const profitColor = c.profit > 5 ? "#00e5a0" : c.profit > 0 ? "#f0c040" : "#e05555";
                return (
                  <tr key={c.name} className={`gem-row ${idx%2===0?"row-even":"row-odd"}`}>
                    <td className="td-rank">{idx+1}</td>
                    <td className="td-name">
                      {c.icon && <img src={c.icon} alt="" className="gem-icon" />}
                      <div>
                        <div>{c.name}</div>
                        <div className="num-level">×{c.stackSize} cards · ~{c.count} listed</div>
                      </div>
                    </td>
                    <td className="td-num">
                      <div className="num-val">{fmtVal(c.totalCost, c.totalCostChaos)}</div>
                      <div className="num-level">{fmtVal(c.cardPrice, c.cardPriceChaos)} each</div>
                    </td>
                    <td className="td-num">
                      <div className="num-val" style={{ color: typeColors[c.rewardType] }}>
                        {fmtVal(c.rewardValue, c.rewardValueChaos)}
                      </div>
                    </td>
                    <td className="td-num">
                      <div className="num-val" style={{ color: profitColor, fontWeight: 700 }}>
                        {c.profit >= 0 ? "+" : ""}{fmt(c.profit)}d
                      </div>
                    </td>
                    <td className="td-num">
                      <div className="num-val" style={{ color: profitColor }}>
                        {c.profitPct >= 0 ? "+" : ""}{c.profitPct.toFixed(0)}%
                      </div>
                    </td>
                    <td className="td-num" style={{ fontSize: 11, color: typeColors[c.rewardType] }}>
                      <div>{c.rewardText}</div>
                      <div className="num-level">{c.rewardType}</div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} pageSize={pageSize}
        total={filtered.length} onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />
    </div>
  );
}

// ── BOSSING PROFITABILITY TAB ─────────────────────────────────────────────────
interface BossResult extends Boss {
  entryCostDiv: number;
  dropEV: number;
  netEV: number;
  pricesLoaded: boolean;
}

async function fetchBossPrices(league = "Allflame"): Promise<Record<string, { div: number; chaos: number }>> {
  const [uniquesRes, gemsRes, currencyRes, fragmentsRes] = await Promise.all([
    fetch(`/api/uniques?league=${encodeURIComponent(league)}`).then(r => r.json()),
    fetch(`/api/gems?league=${encodeURIComponent(league)}`).then(r => r.json()),
    fetch(`/api/currency?league=${encodeURIComponent(league)}`).then(r => r.json()),
    fetch(`/api/jewels?league=${encodeURIComponent(league)}`).then(r => r.json()),
  ]);

  const prices: Record<string, { div: number; chaos: number }> = {};

  // Currency
  const divChaos = (currencyRes.lines ?? []).find((l: any) => l.currencyTypeName === "Divine Orb")?.chaosEquivalent ?? 267;
  for (const c of currencyRes.lines ?? []) {
    if (!c.currencyTypeName) continue;
    prices[c.currencyTypeName.toLowerCase()] = { div: c.chaosEquivalent / divChaos, chaos: c.chaosEquivalent };
  }

  // Uniques
  for (const u of uniquesRes.lines ?? []) {
    const key = u.name.toLowerCase();
    if (!prices[key] || u.divineValue < prices[key].div) {
      prices[key] = { div: u.divineValue ?? 0, chaos: u.chaosValue ?? 0 };
    }
  }

  // Forbidden Jewels
  for (const j of fragmentsRes.lines ?? []) {
    const meta = j.metadata ?? {};
    const key = (meta.passiveName ?? j.name ?? "").toLowerCase();
    if (key && (!prices[key] || j.divineValue < prices[key].div)) {
      prices[key] = { div: j.divineValue ?? 0, chaos: j.chaosValue ?? 0 };
    }
  }

  // Gems (lv1 non-corrupted as entry cost reference; max level for drops)
  for (const g of gemsRes.lines ?? []) {
    if (g.corrupted) continue;
    const key = g.name.toLowerCase();
    if (!prices[key] || g.divineValue > prices[key].div) {
      prices[key] = { div: g.divineValue ?? 0, chaos: g.chaosValue ?? 0 };
    }
  }

  // Fragment prices from currency API
  for (const line of currencyRes.lines ?? []) {
    prices[line.currencyTypeName.toLowerCase()] = {
      div: line.chaosEquivalent / divChaos,
      chaos: line.chaosEquivalent,
    };
  }

  prices["divine orb"] = { div: 1, chaos: divChaos };
  return prices;
}

// ── Drop price overrides
const DROP_MANUAL_KEYS: { key: string; label: string }[] = [
  { key: "watchers2",      label: "Watcher's Eye (2-mod)" },
  { key: "watchers3",      label: "Watcher's Eye (3-mod)" },
  { key: "forbiddenFlame", label: "Forbidden Flame (avg)" },
  { key: "forbiddenFlesh", label: "Forbidden Flesh (avg)" },
  { key: "lightOfMeaning", label: "The Light of Meaning"  },
  { key: "boundByDestiny", label: "Bound By Destiny"       },
];

// ── Entry cost fragment overrides (optional, overrides API price)
const FRAG_MANUAL_KEYS: { key: string; label: string; fragName: string }[] = [
  { key: "frag_alhezmins_crest",           label: "Al-Hezmin's Crest",          fragName: "Al-Hezmin's Crest" },
  { key: "frag_awakening_fragment",        label: "Awakening Fragment",          fragName: "Awakening Fragment" },
  { key: "frag_barans_crest",              label: "Baran's Crest",               fragName: "Baran's Crest" },
  { key: "frag_blazing_fragment",          label: "Blazing Fragment",            fragName: "Blazing Fragment" },
  { key: "frag_cosmic_fragment",           label: "Cosmic Fragment",             fragName: "Cosmic Fragment" },
  { key: "frag_decaying_fragment",         label: "Decaying Fragment",           fragName: "Decaying Fragment" },
  { key: "frag_devouring_fragment",        label: "Devouring Fragment",          fragName: "Devouring Fragment" },
  { key: "frag_droxs_crest",               label: "Drox's Crest",                fragName: "Drox's Crest" },
  { key: "frag_echo_of_loneliness",        label: "Echo of Loneliness",          fragName: "Echo of Loneliness" },
  { key: "frag_echo_of_reverence",         label: "Echo of Reverence",           fragName: "Echo of Reverence" },
  { key: "frag_echo_of_trauma",            label: "Echo of Trauma",              fragName: "Echo of Trauma" },
  { key: "frag_fragment_of_constriction",  label: "Fragment of Constriction",    fragName: "Fragment of Constriction" },
  { key: "frag_fragment_of_emptiness",     label: "Fragment of Emptiness",       fragName: "Fragment of Emptiness" },
  { key: "frag_fragment_of_enslavement",   label: "Fragment of Enslavement",     fragName: "Fragment of Enslavement" },
  { key: "frag_fragment_of_eradication",   label: "Fragment of Eradication",     fragName: "Fragment of Eradication" },
  { key: "frag_fragment_of_knowledge",     label: "Fragment of Knowledge",       fragName: "Fragment of Knowledge" },
  { key: "frag_fragment_of_purification",  label: "Fragment of Purification",    fragName: "Fragment of Purification" },
  { key: "frag_fragment_of_shape",         label: "Fragment of Shape",           fragName: "Fragment of Shape" },
  { key: "frag_fragment_of_terror",        label: "Fragment of Terror",          fragName: "Fragment of Terror" },
  { key: "frag_fragment_of_the_chimera",   label: "Fragment of the Chimera",     fragName: "Fragment of the Chimera" },
  { key: "frag_fragment_of_the_hydra",     label: "Fragment of the Hydra",       fragName: "Fragment of the Hydra" },
  { key: "frag_fragment_of_the_minotaur",  label: "Fragment of the Minotaur",    fragName: "Fragment of the Minotaur" },
  { key: "frag_fragment_of_the_phoenix",   label: "Fragment of the Phoenix",     fragName: "Fragment of the Phoenix" },
  { key: "frag_incandescent_invitation",   label: "Incandescent Invitation",     fragName: "Incandescent Invitation" },
  { key: "frag_lonely_fragment",           label: "Lonely Fragment",             fragName: "Lonely Fragment" },
  { key: "frag_mavens_writ",               label: "The Maven's Writ",             fragName: "The Maven's Writ" },
  { key: "frag_reality_fragment",          label: "Reality Fragment",            fragName: "Reality Fragment" },
  { key: "frag_reverent_fragment",         label: "Reverent Fragment",           fragName: "Reverent Fragment" },
  { key: "frag_screaming_invitation",      label: "Screaming Invitation",        fragName: "Screaming Invitation" },
  { key: "frag_the_audience_with_the_king",label: "An Audience With The King",   fragName: "An Audience With The King" },
  { key: "frag_traumatic_fragment",        label: "Traumatic Fragment",          fragName: "Traumatic Fragment" },
  { key: "frag_veritanias_crest",          label: "Veritania's Crest",           fragName: "Veritania's Crest" },
];

const MANUAL_KEYS = [...DROP_MANUAL_KEYS, ...FRAG_MANUAL_KEYS];

function BossingTab({ onRefreshReady }: { onRefreshReady?: OnRefreshReady }) {
  const [apiPrices, setApiPrices] = useState<Record<string, { div: number; chaos: number }>>({});
  const [divChaos, setDivChaos]   = useState(267);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [category, setCategory]   = useState("All");
  const [sortKey, setSortKey]     = useState<"name"|"entryCostDiv"|"dropEV"|"netEV">("netEV");
  const [sortDir, setSortDir]     = useState<"asc"|"desc">("desc");

  // defined at module level as MANUAL_KEYS

  const [manualPrices, setManualPrices] = useState<Record<string, { value: string; mode: "div"|"chaos" }>>(() =>
    Object.fromEntries(MANUAL_KEYS.map((k) => [k.key, { value: "", mode: "div" as const }]))
  );

  const doFetchBossing = useCallback(async () => {
    const [p, jewelsData] = await Promise.all([
      fetchBossPrices(),
      fetch(`/api/jewels`).then(r => r.json()).catch(() => ({ lines: [] })),
    ]);
    setApiPrices(p);
    setDivChaos(p["divine orb"]?.chaos ?? 267);
    const lines = jewelsData.lines ?? [];
    const flames = lines.filter((j: any) => (j.variant ?? "").toLowerCase().includes("forbidden flame"));
    const flesh  = lines.filter((j: any) => (j.variant ?? "").toLowerCase().includes("forbidden flesh"));
    const avgFlame = flames.length > 0 ? flames.reduce((s: number, j: any) => s + (j.divineValue ?? 0), 0) / flames.length : 0;
    const avgFlesh = flesh.length  > 0 ? flesh.reduce((s: number,  j: any) => s + (j.divineValue ?? 0), 0) / flesh.length  : 0;
    setManualPrices(prev => ({
      ...prev,
      forbiddenFlame: prev.forbiddenFlame.value ? prev.forbiddenFlame : { value: avgFlame > 0 ? avgFlame.toFixed(2) : "", mode: "div" },
      forbiddenFlesh: prev.forbiddenFlesh.value ? prev.forbiddenFlesh : { value: avgFlesh > 0 ? avgFlesh.toFixed(2) : "", mode: "div" },
    }));
    setLoading(false);
  }, []); // eslint-disable-line
  const bossingRefresh = useAutoRefresh(doFetchBossing);
  useEffect(() => {
    if (onRefreshReady) onRefreshReady({
      loading,
      lastUpdate: bossingRefresh.lastUpdate,
      countdownStr: bossingRefresh.countdownStr,
      refreshMins: bossingRefresh.refreshMins,
      setRefreshMins: bossingRefresh.setRefreshMins,
      onRefresh: bossingRefresh.doRefresh,
    });
  }, [bossingRefresh.lastUpdate, bossingRefresh.countdownStr, bossingRefresh.refreshMins, loading, onRefreshReady]); // eslint-disable-line
  useEffect(() => { doFetchBossing(); }, []); // eslint-disable-line

  function manualDiv(key: string): number {
    const m = manualPrices[key];
    if (!m || m.value === "") return 0;
    const v = parseFloat(m.value);
    if (isNaN(v) || v <= 0) return 0;
    return m.mode === "div" ? v : v / divChaos;
  }

  // Map drop name → manual key
  const DROP_TO_MANUAL: Record<string, string> = {
    "watcher's eye (2-mod)": "watchers2",
    "watcher's eye (3-mod)": "watchers3",
    "watcher's eye":         "watchers2", // elder drops 2-mod
    "forbidden flame":       "forbiddenFlame",
    "forbidden flesh":       "forbiddenFlesh",
    "the light of meaning":  "lightOfMeaning",
    "bound by destiny":      "boundByDestiny",
  };

  const results: BossResult[] = BOSSES.map((boss) => {
    // Build frag name→manual key map for quick lookup
    const fragNameToKey: Record<string, string> = {};
    for (const fk of FRAG_MANUAL_KEYS) {
      fragNameToKey[fk.fragName.toLowerCase()] = fk.key;
    }

    let entryCostDiv = 0;
    for (const cost of boss.entryCost) {
      const manualKey = fragNameToKey[cost.name.toLowerCase()];
      const manualVal = manualKey ? manualDiv(manualKey) : 0;
      const apiVal    = apiPrices[cost.name.toLowerCase()]?.div ?? 0;
      const priceDiv  = manualVal > 0 ? manualVal : apiVal;
      entryCostDiv += priceDiv * cost.qty;
    }
    let dropEV = 0;
    for (const drop of boss.drops) {
      const manualKey = DROP_TO_MANUAL[drop.name.toLowerCase()];
      const priceDiv = manualKey
        ? manualDiv(manualKey)
        : (apiPrices[drop.name.toLowerCase()]?.div ?? 0);
      dropEV += drop.prob * drop.qty * priceDiv;
    }
    return { ...boss, entryCostDiv, dropEV, netEV: dropEV - entryCostDiv, pricesLoaded: !loading };
  });

  const categories = ["All", ...Array.from(new Set(BOSSES.map((b) => b.category)))];

  const filtered = results
    .filter((b: BossResult) => category === "All" || b.category === category)
    .filter((b) => !search.trim() || b.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const av = a[sortKey] as any, bv = b[sortKey] as any;
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? av - bv : bv - av;
    });

  function handleSort(key: typeof sortKey) {
    if (key === sortKey) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  }

  return (
    <div>
      {/* Manual price overrides */}
      <div className="manual-prices-panel">
        <div className="qty-title">✎ Drop Prices</div>
        <div className="manual-prices-grid">
          {DROP_MANUAL_KEYS.map((k) => {
            const m = manualPrices[k.key];
            const numVal = parseFloat(m.value);
            const hasVal = m.value !== "" && !isNaN(numVal) && numVal > 0;
            return (
              <div key={k.key} className="manual-price-field">
                <label className="manual-price-label">{k.label}</label>
                <div className="manual-price-input-wrap">
                  <input
                    type="number" min="0" step="0.01"
                    className="manual-price-input"
                    placeholder="0"
                    value={m.value}
                    onChange={(e) => setManualPrices(prev => ({ ...prev, [k.key]: { ...prev[k.key], value: e.target.value } }))}
                  />
                  <button
                    className={`mode-toggle ${m.mode === "div" ? "mode-div" : "mode-chaos"}`}
                    onClick={() => setManualPrices(prev => ({
                      ...prev,
                      [k.key]: { ...prev[k.key], mode: prev[k.key].mode === "div" ? "chaos" : "div" }
                    }))}
                  >{m.mode === "div" ? "d" : "c"}</button>
                </div>
                {hasVal && (
                  <div className="manual-price-converted">
                    {m.mode === "div"
                      ? `≈ ${Math.round(numVal * divChaos)}c`
                      : `≈ ${(numVal / divChaos).toFixed(2)}d`}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="qty-title" style={{ marginTop: 8 }}>✎ Entry Cost Overrides <span style={{ fontSize: 11, color: "var(--text-dim)", fontFamily: "sans-serif", textTransform: "none", letterSpacing: 0 }}>(leave blank to use API price)</span></div>
        <div className="manual-prices-grid">
          {FRAG_MANUAL_KEYS.map((k) => {
            const m = manualPrices[k.key];
            const numVal = parseFloat(m.value);
            const hasVal = m.value !== "" && !isNaN(numVal) && numVal > 0;
            const apiVal = apiPrices[k.fragName.toLowerCase()];
            return (
              <div key={k.key} className="manual-price-field">
                <label className="manual-price-label">{k.label}</label>
                <div className="manual-price-input-wrap">
                  <input
                    type="number" min="0" step="0.01"
                    className="manual-price-input"
                    placeholder={apiVal ? (apiVal.div >= 1 ? `${apiVal.div.toFixed(2)}d` : `${Math.round(apiVal.chaos)}c`) : "0"}
                    value={m.value}
                    onChange={(e) => setManualPrices(prev => ({ ...prev, [k.key]: { ...prev[k.key], value: e.target.value } }))}
                  />
                  <button
                    className={`mode-toggle ${m.mode === "div" ? "mode-div" : "mode-chaos"}`}
                    onClick={() => setManualPrices(prev => ({
                      ...prev,
                      [k.key]: { ...prev[k.key], mode: prev[k.key].mode === "div" ? "chaos" : "div" }
                    }))}
                  >{m.mode === "div" ? "d" : "c"}</button>
                </div>
                {hasVal && (
                  <div className="manual-price-converted">
                    {m.mode === "div"
                      ? `≈ ${Math.round(numVal * divChaos)}c`
                      : `≈ ${(numVal / divChaos).toFixed(2)}d`}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="toolbar">
        <div className="search-wrap">
          <span className="search-icon">⌕</span>
          <input className="search" placeholder="Search boss…" value={search}
            onChange={(e) => setSearch(e.target.value)} />
          {search && <button className="search-clear" onClick={() => setSearch("")}>✕</button>}
        </div>
        <div className="colour-filter">
          {categories.map((c) => (
            <button key={c} className={`colour-btn ${category === c ? "cat-btn-active" : ""}`}
              onClick={() => setCategory(c)}>{c}</button>
          ))}
        </div>
        <div className="toolbar-right">
          <div className="result-count">{filtered.length} bosses</div>
          <div className="legend" style={{ fontSize: 11, color: "#7a7570" }}>
            EV = Σ(prob × qty × price) · Net = EV − entry cost · Drop rates from PoE Wiki
          </div>
        </div>
      </div>

      <div className="table-wrap">
        <table className="gem-table">
          <thead>
            <tr>
              <th className="th-rank">#</th>
              <th className={`th-sortable ${sortKey==="name"?"th-active":""}`} onClick={() => handleSort("name")} style={{ textAlign: "left" }}>
                Boss <SortIcon active={sortKey==="name"} dir={sortDir} />
              </th>
              <th className="th-sortable" style={{ color: "#7a7570", fontSize: 11 }}>Category</th>
              <th className={`th-sortable ${sortKey==="entryCostDiv"?"th-active":""}`} onClick={() => handleSort("entryCostDiv")}>
                Entry Cost <SortIcon active={sortKey==="entryCostDiv"} dir={sortDir} />
              </th>
              <th className={`th-sortable ${sortKey==="dropEV"?"th-active":""}`} onClick={() => handleSort("dropEV")}>
                Drop EV <SortIcon active={sortKey==="dropEV"} dir={sortDir} />
              </th>
              <th className={`th-sortable ${sortKey==="netEV"?"th-active":""}`} onClick={() => handleSort("netEV")}>
                Net EV <SortIcon active={sortKey==="netEV"} dir={sortDir} />
              </th>
              <th className="th-sortable" style={{ textAlign: "left" }}>Key Drops</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="loading-cell"><div className="spinner" /><div>Fetching prices…</div></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="empty-cell">No bosses found.</td></tr>
            ) : (
              filtered.map((b, i) => {
                const profitColor = b.netEV > 2 ? "#00e5a0" : b.netEV > 0 ? "#f0c040" : "#e05555";
                return (
                  <tr key={b.name} className={`gem-row ${i%2===0?"row-even":"row-odd"}`}>
                    <td className="td-rank">{i+1}</td>
                    <td className="td-name">
                      <div>
                        <div>{b.name}</div>
                        {b.notes && <div className="num-level">{b.notes}</div>}
                      </div>
                    </td>
                    <td className="td-num" style={{ fontSize: 11, color: "#7a7570" }}>{b.category}</td>
                    <td className="td-num">
                      <div className="num-val" style={{ color: "#e07070" }}>
                        {b.entryCostDiv > 0
                          ? fmtVal(b.entryCostDiv, b.entryCostDiv * divChaos)
                          : "Free"}
                      </div>
                      <div className="num-level">
                        {b.entryCost.map(c => `${c.qty}× ${c.name}`).join(", ")}
                      </div>
                    </td>
                    <td className="td-num">
                      <div className="num-val" style={{ color: "#4a90d9" }}>
                        {fmtVal(b.dropEV, b.dropEV * divChaos)}
                      </div>
                    </td>
                    <td className="td-num">
                      <div className="num-val" style={{ color: profitColor, fontWeight: 700 }}>
                        {b.netEV >= 0 ? "+" : ""}
                        {b.netEV >= 1 || b.netEV <= -1
                          ? fmtVal(b.netEV, b.netEV * divChaos)
                          : <>{Math.round(b.netEV * divChaos)}<span className="currency-label">c</span></>}
                      </div>
                    </td>
                    <td className="td-name" style={{ fontSize: 11 }}>
                      {b.drops.filter(d => d.prob >= 0.05).map((d) => (
                        <span key={d.name} className="drop-tag">
                          {d.name} ({(d.prob*100).toFixed(0)}%)
                        </span>
                      ))}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ── TAB REFRESH STATE ─────────────────────────────────────────────────────────
interface TabRefreshState {
  loading: boolean;
  lastUpdate: Date | null;
  countdownStr: string | null;
  refreshMins: number;
  setRefreshMins: (m: number) => void;
  onRefresh: () => void;
}
type OnRefreshReady = (s: TabRefreshState) => void;


// ── VALDO RUNNER TAB ─────────────────────────────────────────────────────────
interface ValdoMap {
  name: string;
  icon?: string;
  variant: string;
  uniqueName: string;
  mapPrice: number;
  mapPriceChaos: number;
  uniquePrice: number;
  uniquePriceChaos: number;
  profit: number;
  count: number;
  modifiers: string[];
}

async function fetchValdoMaps(): Promise<{ maps: ValdoMap[]; allMods: string[] }> {
  const [valdoRes, uniquesRes] = await Promise.all([
    fetch("/api/valdo").then(r => r.json()),
    fetch("/api/uniques").then(r => r.json()),
  ]);

  // Build unique price lookup: name.toLowerCase() → cheapest div price
  const uniquePrices: Record<string, { div: number; chaos: number }> = {};
  for (const u of uniquesRes.lines ?? []) {
    const key = (u.name ?? "").toLowerCase();
    const div = u.divineValue ?? 0;
    const chaos = u.chaosValue ?? 0;
    if (!uniquePrices[key] || div < uniquePrices[key].div) {
      uniquePrices[key] = { div, chaos };
    }
  }

  const maps: ValdoMap[] = [];
  const allModsSet = new Set<string>();

  for (const raw of valdoRes.lines ?? []) {
    const variant: string = raw.variant ?? "";
    if (!variant.startsWith("Foil ")) continue;
    const uniqueName = variant.slice(5); // remove "Foil "

    const mapDiv   = raw.divineValue  ?? 0;
    const mapChaos = raw.chaosValue   ?? 0;

    const up = uniquePrices[uniqueName.toLowerCase()];
    if (!up) continue;

    // profit = unique_price - map_price (normalized to divine)
    const mapPriceDiv    = mapDiv > 0 ? mapDiv : mapChaos / 267;
    const uniquePriceDiv = up.div;
    const profitDiv = uniquePriceDiv - mapPriceDiv;

    const mods = (raw.explicitModifiers ?? []).map((m: any) => m.text?.split("\n")[0] ?? "");
    mods.forEach((m: string) => allModsSet.add(m));

    maps.push({
      name:             raw.name,
      icon:             raw.icon,
      variant,
      uniqueName,
      mapPrice:         mapPriceDiv,
      mapPriceChaos:    mapChaos,
      uniquePrice:      uniquePriceDiv,
      uniquePriceChaos: up.chaos,
      profit:           profitDiv,
      count:            raw.count ?? 0,
      modifiers:        mods,
    });
  }

  maps.sort((a, b) => b.profit - a.profit);
  const allMods = Array.from(allModsSet).sort();
  return { maps, allMods };
}

function ValdoTab({ onRefreshReady }: { onRefreshReady?: OnRefreshReady }) {
  const [maps, setMaps]           = useState<ValdoMap[]>([]);
  const [allMods, setAllMods]     = useState<string[]>([]);
  const [filtered, setFiltered]   = useState<ValdoMap[]>([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [excludedMods, setExcludedMods] = useState<Set<string>>(new Set());
  const [showDropdown, setShowDropdown] = useState(false);
  const [modSearch, setModSearch] = useState("");
  const [showProfitOnly, setShowProfitOnly] = useState(true);
  const [minListed, setMinListed] = useState(3);
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState(25);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const doFetch = useCallback(async () => {
    setLoading(true);
    try {
      const { maps: m, allMods: am } = await fetchValdoMaps();
      setMaps(m); setAllMods(am);
    } finally { setLoading(false); }
  }, []);

  const valdoRefresh = useAutoRefresh(doFetch);
  useEffect(() => { doFetch(); }, []); // eslint-disable-line
  useEffect(() => {
    if (onRefreshReady) onRefreshReady({
      loading, lastUpdate: valdoRefresh.lastUpdate, countdownStr: valdoRefresh.countdownStr,
      refreshMins: valdoRefresh.refreshMins, setRefreshMins: valdoRefresh.setRefreshMins,
      onRefresh: valdoRefresh.doRefresh,
    });
  }, [valdoRefresh.lastUpdate, valdoRefresh.countdownStr, valdoRefresh.refreshMins, loading, onRefreshReady]); // eslint-disable-line

  // Close dropdown on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node))
        setShowDropdown(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    let result = [...maps];
    // Exclude maps that contain any excluded modifier
    if (excludedMods.size > 0) {
      result = result.filter(m => !m.modifiers.some(mod => excludedMods.has(mod)));
    }
    if (showProfitOnly) result = result.filter(m => m.profit > 0);
    if (minListed > 0) result = result.filter(m => m.count >= minListed);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(m => m.uniqueName.toLowerCase().includes(q) || m.name.toLowerCase().includes(q));
    }
    setFiltered(result);
    setPage(1);
  }, [maps, excludedMods, showProfitOnly, minListed, search]);

  function toggleMod(mod: string) {
    setExcludedMods(prev => {
      const next = new Set(prev);
      next.has(mod) ? next.delete(mod) : next.add(mod);
      return next;
    });
  }

  const filteredMods = modSearch.trim()
    ? allMods.filter(m => m.toLowerCase().includes(modSearch.toLowerCase()))
    : allMods;

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  return (
    <div>
      <div className="toolbar">
        <div className="search-wrap">
          <span className="search-icon">⌕</span>
          <input className="search" placeholder="Search unique or map name…" value={search}
            onChange={e => setSearch(e.target.value)} />
          {search && <button className="search-clear" onClick={() => setSearch("")}>✕</button>}
        </div>

        {/* Modifier exclude dropdown */}
        <div className="valdo-mod-wrap" ref={dropdownRef}>
          <button className={`valdo-mod-btn ${excludedMods.size > 0 ? "active" : ""}`}
            onClick={() => setShowDropdown(v => !v)}>
            🚫 Exclude Mods {excludedMods.size > 0 && `(${excludedMods.size})`} ▾
          </button>
          {showDropdown && (
            <div className="valdo-mod-dropdown">
              <input className="valdo-mod-search" placeholder="Search modifiers…"
                value={modSearch} onChange={e => setModSearch(e.target.value)} />
              <div className="valdo-mod-list">
                {filteredMods.map(mod => (
                  <label key={mod} className={`valdo-mod-item ${excludedMods.has(mod) ? "excluded" : ""}`}>
                    <input type="checkbox" checked={excludedMods.has(mod)}
                      onChange={() => toggleMod(mod)} />
                    <span>{mod}</span>
                  </label>
                ))}
              </div>
              {excludedMods.size > 0 && (
                <button className="valdo-mod-clear" onClick={() => setExcludedMods(new Set())}>
                  Clear all ({excludedMods.size})
                </button>
              )}
            </div>
          )}
        </div>

        <button className={`fav-filter-btn ${showProfitOnly ? "active" : ""}`}
          onClick={() => setShowProfitOnly(v => !v)}>
          Profitable only
        </button>
        <div className="min-count-control">
          <span className="refresh-label">Min listed:</span>
          <input type="range" min={0} max={50} step={1} value={minListed}
            onChange={e => setMinListed(Number(e.target.value))} className="count-slider" />
          <span className="count-value">{minListed}</span>
        </div>
        <div className="toolbar-right">
          <div className="result-count">{filtered.length} maps</div>
          <div className="legend" style={{ fontSize: 11, color: "#7a7570" }}>
            Profit = unique price − map price
          </div>
        </div>
      </div>

      <div className="table-wrap">
        <table className="gem-table">
          <thead>
            <tr>
              <th className="th-rank">#</th>
              <th className="th-sortable" style={{ textAlign: "left" }}>Valdo Map</th>
              <th className="th-sortable">Price</th>
              <th className="th-sortable" style={{ textAlign: "left" }}>Unique</th>
              <th className="th-sortable">Price</th>
              <th className="th-sortable th-active">Profit</th>
              <th className="th-sortable" style={{ textAlign: "left" }}>Modifiers</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="loading-cell"><div className="spinner" /><div>Fetching maps…</div></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="empty-cell">No maps found.</td></tr>
            ) : (
              filtered.slice((page-1)*pageSize, page*pageSize).map((m, i) => {
                const idx = (page-1)*pageSize+i;
                const profitColor = m.profit > 5 ? "#00e5a0" : m.profit > 0 ? "#f0c040" : "#e05555";
                return (
                  <tr key={m.name+idx} className={`gem-row ${idx%2===0?"row-even":"row-odd"}`}>
                    <td className="td-rank">{idx+1}</td>
                    <td className="td-name">
                      {m.icon && <img src={m.icon} alt="" className="gem-icon" />}
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span>{m.name}</span>
                          <a href={`https://www.pathofexile.com/trade/search/Allflame?q=${encodeURIComponent(JSON.stringify({query:{filters:{type_filters:{filters:{category:{option:"map"}}}},status:{option:"available"},term:m.name}}))}`}
                            target="_blank" rel="noreferrer" className="trade-link" title="Search on trade site">🔗</a>
                        </div>
                        <div className="num-level">~{m.count} listed</div>
                      </div>
                    </td>
                    <td className="td-num">
                      <div className="num-val">{fmtVal(m.mapPrice, m.mapPriceChaos)}</div>
                    </td>
                    <td className="td-name">
                      <div style={{ color: "#c8a96e" }}>{m.uniqueName}</div>
                    </td>
                    <td className="td-num">
                      <div className="num-val">{fmtVal(m.uniquePrice, m.uniquePriceChaos)}</div>
                    </td>
                    <td className="td-num">
                      <div className="num-val" style={{ color: profitColor, fontWeight: 700 }}>
                        {m.profit >= 0 ? "+" : ""}{fmtVal(m.profit, m.profit * 267)}
                      </div>
                    </td>
                    <td className="td-name" style={{ fontSize: 11 }}>
                      {m.modifiers.map((mod, mi) => (
                        <span key={mi} className={`drop-tag ${excludedMods.has(mod) ? "mod-excluded" : ""}`}>
                          {mod}
                        </span>
                      ))}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} pageSize={pageSize}
        total={filtered.length} onPage={setPage} onPageSize={s => { setPageSize(s); setPage(1); }} />
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────────────
export default function App() {
  const [tab, setTab]             = useState<Tab>("double");
  const chaosPerDiv = useChaosPerDiv();
  const [tabRefresh, setTabRefresh] = useState<TabRefreshState | null>(null);
  const handleRefreshReady = useCallback((s: TabRefreshState) => setTabRefresh(s), []);
  const [gems, setGems]           = useState<GemResult[]>([]);
  const [filtered, setFiltered]   = useState<GemResult[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [search, setSearch]       = useState("");
  const [sortKey, setSortKey]     = useState<SortKey>("profit");
  const [sortDir, setSortDir]     = useState<SortDir>("desc");
  const [refreshMins, setRefreshMins] = useState(10);
  const [lastUpdate, setLastUpdate]   = useState<Date | null>(null);
  const [countdown, setCountdown]     = useState(0);
  const [overrides, setOverrides]     = useState<Overrides>({});
  const [minCount, setMinCount]       = useState(MIN_COUNT);
  const [bramblebackChaos, setBramblebackChaos] = useState("");
  const [doryansChaos, setDoryansChaos]         = useState("");
  const [showFavsOnly, setShowFavsOnly] = useState(false);
  const [favs, setFavs]           = useState<Set<string>>(() => loadFavs("double-favs"));
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState(25);
  const timerRef     = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  const doFetch = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await fetchAndCompute(minCount);
      setGems(data); setLastUpdate(new Date()); setCountdown(refreshMins * 60);
    } catch { setError("Failed to fetch data from poe.ninja."); }
    finally { setLoading(false); }
  }, [minCount, refreshMins]); // eslint-disable-line

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await fetchAndCompute(minCount);
      setGems((prev) => data.map((d) => {
        const hasOverride = overrides[d.name] && Object.keys(overrides[d.name]).length > 0;
        return hasOverride ? (prev.find((p) => p.name === d.name) ?? d) : d;
      }));
      setLastUpdate(new Date()); setCountdown(refreshMins * 60);
    } catch { setError("Failed to fetch data from poe.ninja."); }
    finally { setLoading(false); }
  }, [minCount, refreshMins, overrides]);

  useEffect(() => { doFetch(); }, []); // eslint-disable-line
  useEffect(() => { if (gems.length > 0) doFetch(); }, [minCount]); // eslint-disable-line

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(refresh, refreshMins * 60 * 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [refreshMins, refresh]);

  useEffect(() => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, [lastUpdate]);

  useEffect(() => {
    const dc = chaosPerDiv > 0 ? chaosPerDiv : 267;
    const brambleDiv = bramblebackChaos !== "" && !isNaN(parseFloat(bramblebackChaos))
      ? parseFloat(bramblebackChaos) / dc : 0;
    const doryaniDiv = doryansChaos !== "" && !isNaN(parseFloat(doryansChaos))
      ? parseFloat(doryansChaos) / dc : 0;
    const AWAKENED_EX = new Set([
      "awakened empower support", "awakened enlighten support", "awakened enhance support",
    ]);
    let result = gems.map((g) => {
      const ov = overrides[g.name];
      const base = ov && Object.keys(ov).length > 0 ? applyOverrides(g, ov) : g;
      const isAwakened = AWAKENED_EX.has(base.name.toLowerCase());
      const brambleCost = brambleDiv * (isAwakened ? 5 : 4);
      return { ...base, profit: base.profit - brambleCost - doryaniDiv };
    });
    if (showFavsOnly) result = result.filter((g) => favs.has(g.name));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((g) => g.name.toLowerCase().includes(q));
    }
    result.sort((a, b) => {
      const aFav = favs.has(a.name) ? 0 : 1;
      const bFav = favs.has(b.name) ? 0 : 1;
      if (aFav !== bFav) return aFav - bFav;
      const av = a[sortKey] as any, bv = b[sortKey] as any;
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? av - bv : bv - av;
    });
    setFiltered(result);
    setPage(1);
  }, [gems, overrides, search, sortKey, sortDir, favs, showFavsOnly, bramblebackChaos, doryansChaos, chaosPerDiv]); // eslint-disable-line

  function handleSort(key: SortKey) {
    if (key === sortKey) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortKey(key); setSortDir("desc"); }
  }

  function toggleFav(name: string) {
    setFavs((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      saveFavs("double-favs", next);
      return next;
    });
  }

  function setOverride(gemName: string, field: OverrideField, value: number) {
    setOverrides((prev) => ({ ...prev, [gemName]: { ...prev[gemName], [field]: value } }));
  }
  function resetOverride(gemName: string, field: OverrideField) {
    setOverrides((prev) => { const u = { ...prev[gemName] }; delete u[field]; return { ...prev, [gemName]: u }; });
  }
  function resetAllOverrides(gemName: string) {
    setOverrides((prev) => { const u = { ...prev }; delete u[gemName]; return u; });
  }

  const overrideCount = Object.values(overrides).filter((o) => Object.keys(o).length > 0).length;
  const totalPages    = Math.max(1, Math.ceil(filtered.length / pageSize));
  const countdownStr  = `${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, "0")}`;

  const cols: { key: SortKey; label: string; title: string }[] = [
    { key: "name",   label: "Gem",     title: "Gem name" },
    { key: "best",   label: "Best ◆",  title: "Max level, best quality, corrupted — 25%" },
    { key: "mid",    label: "Mid ◆",   title: "Max-1 level, quality ≥20, corrupted — 50%" },
    { key: "worst",  label: "Worst ◆", title: "Max-1 level, quality <20, cheapest — 25%" },
    { key: "cost",   label: "Cost",    title: "Cheapest non-corrupted lv1 (capped at worst)" },
    { key: "ev",     label: "EV",      title: "0.25×best + 0.5×mid + 0.25×worst" },
    { key: "profit", label: "Profit",  title: "EV − Cost" },
  ];

  return (
    <div className="app">
      <div className="bg-grid" />
      <header className="header">
        <div className="header-left">
          <div className="logo">
            <span className="logo-gem">◆</span>
            <div>
              <div className="logo-title">GemOracle</div>
              <div className="logo-sub">PoE · Skill Gem Profit Calculator · League: Allflame</div>
            </div>
          </div>
        </div>
        <div className="header-right">
          {tab === "double" ? (
            <>
              {lastUpdate && (
                <div className="update-info">
                  <span className="update-label">Updated {lastUpdate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                  <span className="countdown">↻ {countdownStr}</span>
                </div>
              )}
              <div className="refresh-control">
                <span className="refresh-label">Auto-refresh:</span>
                {REFRESH_OPTIONS.map((m) => (
                  <button key={m} className={`refresh-btn ${refreshMins === m ? "active" : ""}`} onClick={() => setRefreshMins(m)}>{m}m</button>
                ))}
              </div>
              <button className="reload-btn" onClick={() => doFetch()} disabled={loading}>
                {loading ? "⟳" : "↺"} Refresh
              </button>
            </>
          ) : tabRefresh ? (
            <>
              {tabRefresh.lastUpdate && (
                <div className="update-info">
                  <span className="update-label">Updated {tabRefresh.lastUpdate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                  <span className="countdown">↻ {tabRefresh.countdownStr}</span>
                </div>
              )}
              <div className="refresh-control">
                <span className="refresh-label">Auto-refresh:</span>
                {REFRESH_OPTIONS.map((m) => (
                  <button key={m} className={`refresh-btn ${tabRefresh.refreshMins === m ? "active" : ""}`} onClick={() => tabRefresh.setRefreshMins(m)}>{m}m</button>
                ))}
              </div>
              <button className="reload-btn" onClick={tabRefresh.onRefresh} disabled={tabRefresh.loading}>
                {tabRefresh.loading ? "⟳" : "↺"} Refresh
              </button>
            </>
          ) : null}
        </div>
      </header>

      <div className="tabs-bar">
        <div className="tabs">
          <button className={`tab-btn ${tab === "double" ? "tab-active" : ""}`} onClick={() => setTab("double")}>⚡ Double Corrupt</button>
          <button className={`tab-btn ${tab === "lab" ? "tab-active" : ""}`} onClick={() => setTab("lab")}>🏛 Lab Runner</button>
          <button className={`tab-btn ${tab === "jewels" ? "tab-active" : ""}`} onClick={() => setTab("jewels")}>💎 Forbidden Jewels</button>
          <button className={`tab-btn ${tab === "leveling" ? "tab-active" : ""}`} onClick={() => setTab("leveling")}>📈 Gem Leveling</button>
          <button className={`tab-btn ${tab === "hot" ? "tab-active" : ""}`} onClick={() => setTab("hot")}>🔥 Hot Items</button>
          <button className={`tab-btn ${tab === "divcards" ? "tab-active" : ""}`} onClick={() => setTab("divcards")}>🃏 Div Cards</button>
          <button className={`tab-btn ${tab === "bossing" ? "tab-active" : ""}`} onClick={() => setTab("bossing")}>⚔ Bossing</button>
          <button className={`tab-btn ${tab === "valdo" ? "tab-active" : ""}`} onClick={() => setTab("valdo")}>🗺 Valdo Runner</button>
        </div>
        <ChaosConverter chaosPerDiv={chaosPerDiv} />
      </div>

      {tab === "double" && (
        <>
          <div className="toolbar">
            <div className="search-wrap">
              <span className="search-icon">⌕</span>
              <input className="search" placeholder="Filter gems…" value={search} onChange={(e) => setSearch(e.target.value)} />
              {search && <button className="search-clear" onClick={() => setSearch("")}>✕</button>}
            </div>
            <div className="min-count-control">
              <span className="refresh-label">Min listed:</span>
              <input type="range" min={1} max={100} step={1} value={minCount}
                onChange={(e) => setMinCount(Number(e.target.value))} className="count-slider" />
              <span className="count-value">{minCount}</span>
            </div>
            <div className="cost-deduct-control">
              <span className="refresh-label" title="×4 per run (×5 for Awakened Empower/Enhance/Enlighten)">🌿 Brambleback:</span>
              <input type="number" min={0} step={1} className="cost-deduct-input"
                placeholder="0c" value={bramblebackChaos}
                onChange={(e) => setBramblebackChaos(e.target.value)} />
              <span className="refresh-label" style={{ marginLeft: 10 }} title="×1 per run for all gems">🏛 Doryani's:</span>
              <input type="number" min={0} step={1} className="cost-deduct-input"
                placeholder="0c" value={doryansChaos}
                onChange={(e) => setDoryansChaos(e.target.value)} />
            </div>
            <button className={`fav-filter-btn ${showFavsOnly ? "active" : ""}`} onClick={() => setShowFavsOnly((v) => !v)}>
              ★ Favourites {favs.size > 0 && `(${favs.size})`}
            </button>
            <div className="toolbar-right">
              <div className="result-count">{filtered.length} / {gems.length} gems</div>
              {overrideCount > 0 && (
                <div className="override-badge">✎ {overrideCount} overridden · refresh paused</div>
              )}
              {overrideCount > 0 && (
                <button className="reset-all-global-btn" onClick={() => setOverrides({})}>
                  ↺ Reset all
                </button>
              )}
              <div className="legend">
                <span style={{ color: "#00e5a0" }}>■ &gt;10d</span>
                <span style={{ color: "#f0c040" }}>■ &gt;2d</span>
                <span style={{ color: "#aaa" }}>■ &gt;0</span>
                <span style={{ color: "#e05555" }}>■ loss</span>
                <span style={{ color: "#c8a96e" }}>✎ = overridden</span>
                <span style={{ color: "#666" }}>* = fallback</span>
              </div>
            </div>
          </div>

          {error && <div className="error-banner">⚠ {error}</div>}

          <div className="table-wrap">
            <table className="gem-table">
              <thead>
                <tr>
                  <th className="th-rank">#</th>
                  <th style={{ width: 32 }}></th>
                  {cols.map((c) => (
                    <th key={c.key} className={`th-sortable ${sortKey === c.key ? "th-active" : ""}`}
                      onClick={() => handleSort(c.key)} title={c.title}>
                      {c.label}<SortIcon active={sortKey === c.key} dir={sortDir} />
                    </th>
                  ))}
                  <th className="th-actions"></th>
                </tr>
              </thead>
              <tbody>
                {loading && gems.length === 0 ? (
                  <tr><td colSpan={10} className="loading-cell"><div className="spinner" /><div>Fetching data from poe.ninja…</div></td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={10} className="empty-cell">No gems found.</td></tr>
                ) : (
                  filtered.slice((page - 1) * pageSize, page * pageSize).map((g, i) => {
                    const globalIndex = (page - 1) * pageSize + i;
                    const ov = overrides[g.name] ?? {};
                    const displayed = Object.keys(ov).length > 0 ? applyOverrides(g, ov) : g;
                    const hasAnyOverride = Object.keys(ov).length > 0;
                    const isFav = favs.has(g.name);
                    return (
                      <tr key={g.name} className={`gem-row ${globalIndex % 2 === 0 ? "row-even" : "row-odd"} ${hasAnyOverride ? "row-overridden" : ""} ${isFav ? "row-fav" : ""}`}>
                        <td className="td-rank">{globalIndex + 1}</td>
                        <td className="td-fav">
                          <StarBtn active={isFav} onClick={(e) => { e.stopPropagation(); toggleFav(g.name); }} />
                        </td>
                        <td className="td-name">
                          {g.icon && <img src={g.icon} alt="" className="gem-icon" />}
                          <span>{g.name}</span>
                        </td>
                        <EditableCell value={displayed.best} chaos={g.bestChaos} overridden={!!ov.best}
                          levelStr={g.bestLevel_str} count={g.bestCount}
                          onSave={(v) => setOverride(g.name, "best", v)} onReset={() => resetOverride(g.name, "best")} />
                        <EditableCell value={displayed.mid} chaos={g.midChaos} overridden={!!ov.mid}
                          levelStr={g.midLevel_str.replace("*", "")} noData={g.midReal === null} count={g.midCount}
                          onSave={(v) => setOverride(g.name, "mid", v)} onReset={() => resetOverride(g.name, "mid")} />
                        <EditableCell value={displayed.worst} chaos={g.worstChaos} overridden={!!ov.worst}
                          levelStr={g.worstLevel_str.replace("*", "")} noData={g.worstReal === null} count={g.worstCount}
                          onSave={(v) => setOverride(g.name, "worst", v)} onReset={() => resetOverride(g.name, "worst")} />
                        <EditableCell value={displayed.cost} chaos={g.costChaos} overridden={!!ov.cost}
                          decimals={4} count={g.costCount}
                          onSave={(v) => setOverride(g.name, "cost", v)} onReset={() => resetOverride(g.name, "cost")} />
                        <td className="td-num td-ev">
                          <div className="num-val" style={{ color: "#4a90d9" }}>{fmt(displayed.ev)}d</div>
                        </td>
                        <td className="td-profit"><ProfitBadge value={displayed.profit} /></td>
                        <td className="td-actions">
                          {hasAnyOverride && (
                            <button className="reset-all-btn" onClick={() => resetAllOverrides(g.name)}>Reset</button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <div className="page-size-control">
              <span className="page-label">Rows:</span>
              {[10, 25, 50, 100].map((s) => (
                <button key={s} className={`page-size-btn ${pageSize === s ? "active" : ""}`}
                  onClick={() => { setPageSize(s); setPage(1); }}>{s}</button>
              ))}
            </div>
            <div className="page-nav">
              <button className="page-btn" onClick={() => setPage(1)} disabled={page === 1}>«</button>
              <button className="page-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                .reduce<(number | "…")[]>((acc, p, idx, arr) => {
                  if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("…");
                  acc.push(p); return acc;
                }, [])
                .map((p, idx) => p === "…"
                  ? <span key={`d${idx}`} className="page-dots">…</span>
                  : <button key={p} className={`page-btn ${page === p ? "active" : ""}`} onClick={() => setPage(p as number)}>{p}</button>
                )}
              <button className="page-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
              <button className="page-btn" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
            </div>
            <div className="page-info">{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)} of {filtered.length}</div>
          </div>
        </>
      )}

      {tab === "lab" && <LabTab onRefreshReady={handleRefreshReady} />}
      {tab === "jewels" && <JewelsTab onRefreshReady={handleRefreshReady} />}
      {tab === "leveling" && <LevelingTab onRefreshReady={handleRefreshReady} />}
      {tab === "hot" && <HotTab onRefreshReady={handleRefreshReady} />}
      {tab === "divcards" && <DivCardsTab onRefreshReady={handleRefreshReady} />}
      {tab === "bossing" && <BossingTab onRefreshReady={handleRefreshReady} />}
      {tab === "valdo" && <ValdoTab onRefreshReady={handleRefreshReady} />}

      <footer className="footer">
        Data from <a href="https://poe.ninja" target="_blank" rel="noreferrer">poe.ninja</a> · League: Allflame · <span style={{color:"#c8a96e"}}>Click any value to edit</span>
      </footer>
    </div>
  );
}
